<dcr-list-item
  [headline]="mixedResult?.eduContent?.publishedEduContentMetadata?.title"
  [supportingText]="mixedResult?.lastUpdated | humanDateTime: { format: 'numericDateTime' }"
  hero-type="score"
  data-cy="edu-content-result-list-item"
>
  <div [ngSwitch]="mixedResult?.type" slot="hero">
    <ng-container *ngSwitchCase="'sessionDay'">
      <campus-score-level
        *ngIf="mixedResult.attempts.length === 1; else scoreLevelsAttemptsTemplate"
        [scoreLevel]="mixedResult.attempts[0]"
      ></campus-score-level>
      <ng-template #scoreLevelsAttemptsTemplate>
        <campus-score-level-attempts [scoreLevels]="mixedResult.attempts"></campus-score-level-attempts>
      </ng-template>
    </ng-container>

    <ng-container *ngSwitchCase="'session'">
      <campus-score-level [scoreLevel]="mixedResult.data"></campus-score-level>
    </ng-container>

    <campus-score
      *ngSwitchCase="'result'"
      data-cy="pages-results-edu-content-score"
      [class.cursor-pointer]="
        mixedResult.status === 'completed' && (mixedResult.data.needsManualGrading || (mixedResult.data.score && mixedResult.isGradable))
      "
      [result]="mixedResult.data"
      [manualGrading]="mixedResult.data.needsManualGrading || (mixedResult.data.score !== null && mixedResult.isGradable)"
      (click)="
        mixedResult.status === 'completed'
          ? mixedResult.actions[1]?.handler(mixedResult?.eduContent, { forResult: mixedResult.data })
          : undefined
      "
    ></campus-score>
  </div>

  <div class="flex gap-3xs" slot="trailing-content">
    <div class="[ label-overline ]">{{ mixedResult?.eduContent?.level?.name }}</div>
    <div data-cy="results-edu-content-time" *ngIf="mixedResult?.status !== ResultStatusEnum.STATUS_NOT_ATTEMPTED && mixedResult.time !== 0">
      <div class="[ label-large ]">
        {{ mixedResult.time || 0 | humanDateTime: { format: 'duration' } }}
      </div>
      <div class="[ label-small weight-light text-opacity-medium on-surface-variant ]">gewerkt</div>
    </div>

    <ng-container *ngIf="mixedResult?.inTask">
      <dcr-icon [attr.id]="'in-task-' + mixedResult?.id" icon="shared:task" aria-label="Deze oefening werd gemaakt in een taak."></dcr-icon>

      <dcr-plain-tooltip positioning="popover" [anchor]="'in-task-' + mixedResult?.id" label="gemaakt in een taak"></dcr-plain-tooltip>
    </ng-container>

    <ng-container *ngIf="mixedResult?.inBundle">
      <dcr-icon
        [attr.id]="'in-bundle-' + mixedResult?.id"
        icon="shared:bundle"
        aria-label="Deze oefening werd gemaakt in een bundel."
      ></dcr-icon>
      <dcr-plain-tooltip positioning="popover" [anchor]="'in-bundle-' + mixedResult?.id" label="gemaakt in een bundel"></dcr-plain-tooltip>
    </ng-container>
  </div>

  <div *ngIf="mixedResult" slot="trailing-actions">
    <div *ngIf="mixedResult.actions?.length > 1; else singleAction">
      <dcr-inline-icon-button
        [attr.id]="'more-actions-' + mixedResult.id"
        data-cy="edu-content-result-action"
        icon="shared:more"
      ></dcr-inline-icon-button>

      <dcr-menu [anchor]="'more-actions-' + mixedResult.id" positioning="popover">
        <dcr-menu-item
          *ngFor="let action of mixedResult.actions"
          [disabled]="action.disabled"
          (click)="action.handler(mixedResult.eduContent, { forResult: mixedResult.data })"
          [leadingIcon]="'content-open-actions:' + action.icon"
          [label]="action.label"
        ></dcr-menu-item>
      </dcr-menu>
    </div>

    <ng-template #singleAction>
      <ng-container *ngIf="mixedResult.actions?.length">
        <dcr-tonal-button
          *ngIf="mixedResult.actions[0] as action"
          [disabled]="action.disabled"
          data-cy="edu-content-result-action"
          [leadingIcon]="'content-open-actions:' + action.icon"
          [label]="action.label"
          (click)="action.handler(mixedResult.eduContent, { forResult: mixedResult.data })"
        ></dcr-tonal-button>
      </ng-container>
    </ng-template>
  </div>
</dcr-list-item>

<dcr-divider inset></dcr-divider>

<ng-template>
  <div class="[ items-center ]">
    <!-- Group Caption views -->
    <div class="[ flex gap-xs items-center ]">
      <!-- Date -->
      <div
        *ngIf="this.mixedResult?.status !== ResultStatusEnum.STATUS_NOT_ATTEMPTED"
        [ngClass]="[
          '[ icon-size-2xl ]',
          '[ center-content white-space-nowrap  ]',
          '[ label-small secondary-container text-center ]',
          '[ corner-full border border-secondary ]',
          '[ transform group-hover:elevation-2 group-hover:scale-110 group-hover:-rotate-3 transition-all ]',
        ]"
        data-cy="pages-results-edu-content-date"
      >
        <div>{{ mixedResult?.lastUpdated | humanDateTime: { format: 'numericDate' } }}</div>
        <div>{{ mixedResult?.lastUpdated | humanDateTime: { format: 'time' } }}</div>
      </div>

      <!-- Title / Level -->
      <div class="[ stack stack-space-3xs ] [ flex-1 ]" data-cy="pages-results-edu-content-title">
        <div class="[ label-overline ]">{{ mixedResult?.eduContent?.level?.name }}</div>

        <div class="[ body-large weight-semibold ]">
          {{ mixedResult?.eduContent?.publishedEduContentMetadata?.title }}
        </div>
      </div>
    </div>

    <!-- Group Score views -->
    <div class="[ flex-none flex gap-m items-center ]">
      <ng-container [ngSwitch]="mixedResult?.type">
        <ng-container *ngSwitchCase="'sessionDay'">
          <campus-score-level
            *ngIf="mixedResult.attempts.length === 1; else scoreLevelsAttemptsTemplate"
            [scoreLevel]="mixedResult.attempts[0]"
          ></campus-score-level>
          <ng-template #scoreLevelsAttemptsTemplate>
            <campus-score-level-attempts [scoreLevels]="mixedResult.attempts"></campus-score-level-attempts>
          </ng-template>
        </ng-container>

        <ng-container *ngSwitchCase="'session'">
          <campus-score-level [scoreLevel]="mixedResult.data"></campus-score-level>
        </ng-container>

        <campus-score *ngSwitchCase="'result'" [result]="mixedResult.data" data-cy="pages-results-edu-content-score"></campus-score>
      </ng-container>
    </div>

    <!-- Group Meta -->
    <div class="[ cluster ][ text-center ]">
      <div class="[ justify-end ]">
        <!-- In Task / In Bundle -->
        <campus-icon svgIcon="task" *ngIf="mixedResult?.inTask"></campus-icon>

        <campus-icon svgIcon="bundle" *ngIf="mixedResult?.inBundle"></campus-icon>

        <!-- Time -->
        <div
          data-cy="results-edu-content-time"
          *ngIf="this.mixedResult?.status !== ResultStatusEnum.STATUS_NOT_ATTEMPTED && mixedResult.time !== 0"
        >
          <div class="[ label-large ]">
            {{ mixedResult.time || 0 | humanDateTime: { format: 'duration' } }}
          </div>
          <div class="[ label-small weight-light text-opacity-medium on-surface-variant ]">gewerkt</div>
        </div>

        <!-- Actions -->
        <div *ngIf="mixedResult">
          <ng-container *ngIf="mixedResult?.actions?.length > 1; else singleAction">
            <button [matMenuTriggerFor]="moreMenu" inline-icon-button data-cy="edu-content-result-action" icon="context_menu"></button>

            <mat-menu #moreMenu="matMenu">
              <button
                inline-button
                class="[ w-full ]"
                *ngFor="let action of mixedResult?.actions"
                [disabled]="action.disabled"
                (click)="action.handler(mixedResult?.eduContent, { forResult: mixedResult.data })"
                [leadingIcon]="action.icon"
              >
                {{ action.label }}
              </button>
            </mat-menu>
          </ng-container>
        </div>

        <ng-template #singleAction>
          <ng-container *ngIf="mixedResult?.actions?.length">
            <button
              *ngIf="mixedResult?.actions[0] as action"
              [disabled]="action.disabled"
              inline-icon-button
              data-cy="edu-content-result-action"
              [icon]="action.icon"
              (click)="action.handler(mixedResult?.eduContent, { forResult: mixedResult.data })"
            ></button>
          </ng-container>
        </ng-template>
      </div>
    </div>
  </div>

  <campus-state-layer [for]="element" class="[ bg-primary index-pullback m-0 ]"></campus-state-layer>
</ng-template>
